import AccountMenuItems from '@components/ui/AccountMenu/AccountMenuItems.tsx';
import { ChangelogSheet, LATEST_CHANGELOG_VERSION } from '@components/ui/AccountMenu/ChangelogSheet';
import { useGetBillingPortalSessionQuery } from '@dieterApi/stripe/useGetBillingPortalSessionQuery';
import { User } from '@dieterApi/user/useUserQuery';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { UserRoleChip } from '../UserRoleChip/UserRoleChip';
import './accountmenu.sass';
import { DeleteAccountModal } from './DeleteAccountModal';
import { useNavigation } from '@app/hooks/useNavigation';
// code-snippet from https://mui.com/material-ui/react-menu/

interface Props {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  user?: User | null;
  onOpenManageSubscription?: () => void;
}

export default function AccountMenu({ anchorEl, handleClose, user, onOpenManageSubscription }: Props) {
  const { t } = useTranslation();
  const { navigation, setNavigation } = useNavigation();
  const [getPortalSession, { data, loading }] = useGetBillingPortalSessionQuery();
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = React.useState(false);
  const [changelogOpen, setChangelogOpen] = React.useState(false);
  const portalUrl = data?.getBillingPortalSession;

  const hasUnseenChangelog = navigation.changelogLastSeenVersion !== LATEST_CHANGELOG_VERSION;

  const handleOpenChangelog = () => {
    setChangelogOpen(true);
    setNavigation((nav) => void (nav.changelogLastSeenVersion = LATEST_CHANGELOG_VERSION));
  };

  const open = Boolean(anchorEl);

  React.useEffect(() => {
    if (open) {
      getPortalSession();
    }
  }, [anchorEl]);

  const userName = user?.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'N N';

  return (
    <React.Fragment>
      <Menu
        className="dtAccountMenu"
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        //onClick={handleClose}
        slotProps={{
          root: {
            sx: {
              zIndex: 1300,
            },
          },
          paper: {
            elevation: 0,
            sx: {
              // The arrow below is drawn half outside the paper's box, so the
              // paper must not clip. That is why this is `visible` and not
              // `auto` — but it also defeats the `overflow-y: auto` that MUI
              // would otherwise pair with its own `max-height: calc(100% -
              // 96px)`. Without a scroll container the menu just spilled past
              // that cap: on a short viewport the last items — logout among
              // them — rendered below the fold with no way to scroll to them.
              //
              // So the list, not the paper, is the scroll container. Making the
              // paper a flex column lets MUI's existing viewport-aware cap do
              // the work, instead of hardcoding a pixel height that is itself
              // wrong on a shorter screen (see TenantMenu's `maxHeight: 600px`).
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
              '& .MuiList-root': {
                overflowY: 'auto',
                // Flex items refuse to shrink below their content height until
                // this is 0, which is what actually arms the scrolling.
                minHeight: 0,
              },
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {!(!user || user.isAnonymous) && (
          <div>
            <div className=" px-4 flex flex-col gap-2 mb-3">
              <span className="text-primary-root">{userName}</span>{' '}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">{t('common.role')}</span>
                <UserRoleChip role={user?.role} />
              </div>
            </div>
            <Divider />
          </div>
        )}
        <AccountMenuItems
          user={user}
          onClose={handleClose}
          portalUrl={portalUrl}
          loading={loading}
          onOpenManageSubscription={onOpenManageSubscription}
          onOpenChangelog={handleOpenChangelog}
          onOpenDeleteAccount={() => setOpenDeleteAccountModal(true)}
          hasUnseenChangelog={hasUnseenChangelog}
        />
      </Menu>
      <DeleteAccountModal open={openDeleteAccountModal} onClose={() => setOpenDeleteAccountModal(false)} />
      <ChangelogSheet open={changelogOpen} onOpenChange={setChangelogOpen} />
    </React.Fragment>
  );
}
